import { REGISTERED_MODULES } from "../modules/index.js";
import { composeRoleUi } from "../modules/compose.js";
import { DEFAULT_WIDGET_ORDER } from "./widgets.js";
/**
 * Priority bands, one per intent.
 *
 * A role that names a daily STATION outranks one that names oversight:
 * the station is where its holder's work waits, and oversight is
 * reachable from anywhere. Roles sharing an intent share a band and are
 * separated by the `code` tiebreak, which is unobservable in the route
 * they both resolve to.
 *
 * Spaced by ten so a band can be inserted between two others.
 */
const PRIORITY = {
    sell: 90,
    verify: 80,
    hr: 70,
    stock: 60,
    cabinet: 55,
    book: 50,
    steer: 10,
};
/**
 * The role UI registry — one entry per code in `SYSTEM_ROLES`.
 *
 * A code present here and absent there (or the reverse) is a typo that no
 * landing test can see, because an unknown code is a no-op by design
 * (I-RP2). The two registries are cross-checked in packages/api, the only
 * package that reads both.
 *
 * Every code declares a `widgets` station, because `/dashboard/summary`
 * now serves one: a verification queue depth, a payroll run status, a
 * till total and the budget chain are all figures the API returns. The
 * resolver still falls back to the whole board for an unknown code
 * (I-RP2), and each key is still gated on its own permission at render
 * and again server-side (I-RP1).
 */
/** Every widget the three firm-delegation roles hold, ledger blocks first. */
const FIRM_DELEGATION_WIDGETS = [
    "alerts",
    "kpi-month-invoices",
    "kpi-revenue",
    "kpi-receivables",
    "treasury",
    "kpi-margin",
    "quick-actions",
    "accounting-health",
    "recent-invoices",
    "establishments",
    "top-articles",
];
const BASE_ROLE_UI_PROFILES = {
    // Steering roles come for the overview, which is the dashboard entire.
    // Declared rather than omitted so that holding one alongside a station
    // role UNIONS to the whole board instead of narrowing to the station.
    ADMIN: {
        code: "ADMIN",
        landingIntent: "steer",
        uiPriority: PRIORITY.steer,
        widgets: [
            ...DEFAULT_WIDGET_ORDER.slice(0, 5),
            "kpi-revenue",
            "kpi-receivables",
            "treasury",
            "kpi-margin",
            "establishments",
            "quick-actions",
            "recent-invoices",
            "top-articles",
            "accounting-health",
            "client-breakdown",
            "article-breakdown",
        ],
    },
    MANAGER: {
        code: "MANAGER",
        landingIntent: "steer",
        uiPriority: PRIORITY.steer,
        widgets: [
            ...DEFAULT_WIDGET_ORDER.slice(0, 5),
            "kpi-revenue",
            "kpi-receivables",
            "treasury",
            "kpi-margin",
            "stock-overview",
            "mcf-doubt",
            "quick-actions",
            "recent-invoices",
            "top-articles",
            "client-breakdown",
            "article-breakdown",
        ],
        groupOrder: ["reports-hub"],
    },
    ACCOUNTANT: {
        code: "ACCOUNTANT",
        landingIntent: "book",
        uiPriority: PRIORITY.book,
        // The invoice flow feeds the VT journal, and the ledger blocks answer
        // the question the closing screen asks — what is left to post.
        widgets: [
            "alerts",
            "kpi-month-invoices",
            "kpi-revenue",
            "kpi-receivables",
            "treasury",
            "kpi-margin",
            "quick-actions",
            "accounting-health",
            "recent-invoices",
        ],
        groupOrder: ["accounting"],
        defaultOpenGroups: ["accounting"],
    },
    // The scopes a client delegates to an accounting firm (role/firm-delegation.ts).
    // A firm session works the books, so all three land on accounting.
    DELEGATION_CONSULTATION: {
        code: "DELEGATION_CONSULTATION",
        landingIntent: "book",
        uiPriority: PRIORITY.book,
        widgets: FIRM_DELEGATION_WIDGETS,
        groupOrder: ["accounting"],
        defaultOpenGroups: ["accounting"],
    },
    DELEGATION_TENUE: {
        code: "DELEGATION_TENUE",
        landingIntent: "book",
        uiPriority: PRIORITY.book,
        widgets: FIRM_DELEGATION_WIDGETS,
        groupOrder: ["accounting"],
        defaultOpenGroups: ["accounting"],
    },
    DELEGATION_TENUE_DECLARATIONS: {
        code: "DELEGATION_TENUE_DECLARATIONS",
        landingIntent: "book",
        uiPriority: PRIORITY.book,
        widgets: FIRM_DELEGATION_WIDGETS,
        groupOrder: ["accounting"],
        defaultOpenGroups: ["accounting"],
    },
    VERIFIER: {
        code: "VERIFIER",
        landingIntent: "verify",
        uiPriority: PRIORITY.verify,
        // The verification queue leads, but VERIFIER also holds `clients:read`
        // and `articles:read` directly (not merely a broader permission that
        // happens to satisfy them) — the station adds `verification-queue`
        // on top of the full reach those two permissions already grant,
        // rather than narrowing to the queue alone.
        widgets: [
            ...DEFAULT_WIDGET_ORDER.slice(0, 5),
            "verification-queue",
            "kpi-revenue",
            "kpi-receivables",
            "top-articles",
            "establishments",
            "quick-actions",
            "recent-invoices",
            "client-breakdown",
            "article-breakdown",
        ],
        groupOrder: ["ventes-caisse"],
        defaultOpenGroups: ["ventes-caisse"],
    },
    CASHIER: {
        code: "CASHIER",
        landingIntent: "sell",
        uiPriority: PRIORITY.sell,
        // The till station: what this operator sold, what their drawer holds
        // and which tickets are still open. All three are owner-scoped.
        widgets: [
            "alerts",
            "kpi-month-invoices",
            "my-sales",
            "pending-tickets",
            "till-today",
            "quick-actions",
            "recent-invoices",
        ],
        groupOrder: ["ventes-caisse"],
        defaultOpenGroups: ["ventes-caisse"],
    },
    STOCK_MANAGER: {
        code: "STOCK_MANAGER",
        landingIntent: "stock",
        uiPriority: PRIORITY.stock,
        widgets: [
            "alerts",
            "kpi-low-stock",
            "kpi-articles",
            "stock-overview",
            "stock-movements-today",
            "quick-actions",
            "near-expiry",
            "article-breakdown",
        ],
        groupOrder: ["catalogue"],
        defaultOpenGroups: ["catalogue"],
    },
    // The counter comes for the inventory sessions. It holds no `stock:read`,
    // so the `stock` intent would be refused: it has its own.
    INVENTORY_COUNTER: {
        code: "INVENTORY_COUNTER",
        landingIntent: "count",
        uiPriority: PRIORITY.stock,
        widgets: ["alerts", "kpi-month-invoices", "quick-actions", "recent-invoices"],
        groupOrder: ["catalogue"],
        defaultOpenGroups: ["catalogue"],
    },
    RH: {
        code: "RH",
        landingIntent: "hr",
        uiPriority: PRIORITY.hr,
        // The HR trio and payroll lead, but the three other permission-free
        // widgets (`alerts`, `kpi-month-invoices`, `recent-invoices`, next to
        // `quick-actions`) and `establishments` (RH holds `establishments:read`
        // directly) stay on the list too — the station adds focus, it does
        // not drop reach.
        widgets: [
            "alerts",
            "kpi-month-invoices",
            "hr-headcount",
            "hr-leave",
            "hr-contracts",
            "establishments",
            "quick-actions",
            "payroll-status",
            "recent-invoices",
        ],
        groupOrder: ["hr"],
        defaultOpenGroups: ["hr"],
    },
    DG: {
        code: "DG",
        landingIntent: "steer",
        uiPriority: PRIORITY.steer,
        widgets: [
            ...DEFAULT_WIDGET_ORDER.slice(0, 5),
            "kpi-revenue",
            "kpi-receivables",
            "treasury",
            "kpi-margin",
            "stock-overview",
            "budget-execution",
            "quick-actions",
            "recent-invoices",
            "top-articles",
            "accounting-health",
            "client-breakdown",
            "article-breakdown",
        ],
        groupOrder: ["reports-hub"],
    },
    // The public-expenditure chain: each acts on the budget through its own
    // screen, and none of the four has a landing surface of its own yet.
    // The four read the same two blocks: `budget:read` is on all of them,
    // and the acts they differ on are queues to work, not figures to read.
    ORDONNATEUR: {
        code: "ORDONNATEUR",
        landingIntent: "steer",
        uiPriority: PRIORITY.steer,
        widgets: ["budget-execution", "quick-actions", "budget-queues"],
        groupOrder: ["budget"],
        defaultOpenGroups: ["budget"],
    },
    CONTROLEUR_FINANCIER: {
        code: "CONTROLEUR_FINANCIER",
        landingIntent: "steer",
        uiPriority: PRIORITY.steer,
        widgets: ["budget-execution", "quick-actions", "budget-queues"],
        groupOrder: ["budget"],
        defaultOpenGroups: ["budget"],
    },
    // Settles mandates. Holds `budget:read` and `budget:pay` — and no
    // `accounting:*`, so the ledger blocks stay out of its list.
    COMPTABLE_PUBLIC: {
        code: "COMPTABLE_PUBLIC",
        landingIntent: "book",
        uiPriority: PRIORITY.book,
        widgets: [
            "alerts",
            "kpi-month-invoices",
            "budget-execution",
            "budget-queues",
            "quick-actions",
            "recent-invoices",
        ],
        groupOrder: ["accounting"],
        defaultOpenGroups: ["accounting"],
    },
    AUTORITE_BUDGETAIRE: {
        code: "AUTORITE_BUDGETAIRE",
        landingIntent: "steer",
        uiPriority: PRIORITY.steer,
        widgets: ["budget-execution", "quick-actions", "budget-queues"],
        groupOrder: ["budget"],
        defaultOpenGroups: ["budget"],
    },
    RESPONSABLE_FINANCIER_PROJET: {
        code: "RESPONSABLE_FINANCIER_PROJET",
        landingIntent: "steer",
        uiPriority: PRIORITY.steer,
        widgets: [
            "budget-execution",
            "treasury",
            "quick-actions",
            "budget-queues",
            "accounting-health",
        ],
        groupOrder: ["projects", "budget"],
        defaultOpenGroups: ["projects"],
    },
    // The accounting firm's staff: the client files are the station. The
    // role holds no company figure, so the board keeps the shortcuts only.
    CABINET_COMPTABLE: {
        code: "CABINET_COMPTABLE",
        landingIntent: "cabinet",
        uiPriority: PRIORITY.cabinet,
        widgets: ["quick-actions"],
        groupOrder: ["cabinet"],
    },
    // Reception and secretariat: the invoices are the station, reached from
    // the « Ventes & Caisse » group. The shortcuts are shown as to every
    // role; the API refuses the creates the role does not hold.
    SECRETARIAT_ACCUEIL: {
        code: "SECRETARIAT_ACCUEIL",
        landingIntent: "steer",
        uiPriority: PRIORITY.steer,
        widgets: ["alerts", "kpi-month-invoices", "quick-actions", "recent-invoices"],
        groupOrder: ["ventes-caisse"],
        defaultOpenGroups: ["ventes-caisse"],
    },
    VIEWER: {
        code: "VIEWER",
        landingIntent: "steer",
        uiPriority: PRIORITY.steer,
        // Reads across the business and acts on nothing.
        widgets: [
            ...DEFAULT_WIDGET_ORDER.slice(0, 5),
            "kpi-revenue",
            "kpi-receivables",
            "stock-overview",
            "quick-actions",
            "recent-invoices",
            "top-articles",
            "near-expiry",
            "client-breakdown",
            "article-breakdown",
        ],
    },
};
export function getRoleUiProfile(code) {
    return ROLE_UI_PROFILES[code] ?? null;
}
/**
 * Core's own role UI profiles plus those the modules contribute. A module
 * describes how its roles land and what they see; the permissions those
 * roles carry stay in the API's own registry, which core cannot import.
 */
export const ROLE_UI_PROFILES = composeRoleUi(BASE_ROLE_UI_PROFILES, REGISTERED_MODULES);
//# sourceMappingURL=profiles.js.map